import type { MetaDescriptor } from 'react-router';
import type { TFunction } from 'i18next';
import { ProductStatus, type Product } from '@linktivity/ars-neo';
import { markdownToPlainText } from '@link/share/components/ReactMarkdown';
import { LOCALES, type Locale } from '@link/share/i18n';
import {
  buildLocalizedPath,
  getActivityCategoryTags,
  productPageJsonLd,
  resolveUnitAmount
} from '@link/share/utils';
import { triplaboSeo } from '../../seo';
import type { ProductPageData } from './product.server';

/**
 * Search snippets are cut at roughly this length, and the activity
 * description is long-form Markdown. The SPA sent the raw Markdown — `**`,
 * list markers and inline HTML included — as the meta description; it is
 * flattened to text and trimmed here instead.
 */
const DESCRIPTION_MAX_LENGTH = 160;

function summarize(markdown: string): string {
  const text = markdownToPlainText(markdown).replace(/\s+/g, ' ').trim();
  // Spread so the cut lands on a code point, not in the middle of a surrogate
  // pair.
  const chars = [...text];
  return chars.length > DESCRIPTION_MAX_LENGTH
    ? `${chars.slice(0, DESCRIPTION_MAX_LENGTH - 1).join('')}…`
    : text;
}

/**
 * The locales this product has a version in, in the API's order.
 *
 * Products are translated individually, so the site-wide locale list would
 * advertise alternates that only fall back to another language. Codes the SPA
 * does not route (anything outside LOCALES) are dropped, and a product that
 * declares none is treated as existing only in the locale being rendered.
 */
export function supportedLocales(product: Product): Locale[] {
  return (product.supportedLanguageCodes ?? [])
    .map(code => code.toLowerCase())
    .filter((code): code is Locale => LOCALES.includes(code as Locale));
}

function productLocales(product: Product, locale: Locale): Locale[] {
  const supported = supportedLocales(product);
  return supported.length > 0 ? supported : [locale];
}

type ProductMetaInput = ProductPageData & { locale: Locale; url: string };

/**
 * Meta tags and JSON-LD for a product page.
 *
 * Mirrors the SPA's `triplaboSeo()` + `useProductStructuredData()` pair, with
 * three changes that only make sense once the tags are in the server HTML:
 *
 * - `noindex, follow` for unlisted or inactive products and for locales the
 *   product is not translated into. The SPA indexed all of them: unlisted
 *   products are reachable by direct link by design, and an untranslated
 *   locale renders fallback copy that would compete with the real version.
 * - The offer price comes from the product's plan units rather than from the
 *   first bookable day of the availability calendar, which the server does not
 *   fetch. Plan units carry the list price, which is what a crawler should
 *   see; a date-specific price is a booking-time detail.
 * - Stock follows the product status for the same reason — the SPA derived it
 *   from calendar availability.
 */
export function buildProductMeta(
  { product, site, currency, locale, url }: ProductMetaInput,
  t: TFunction
): MetaDescriptor[] {
  const activity = product.productContent?.activity;
  const title = activity?.title ?? '';
  const description = summarize(activity?.description ?? '');
  const images = activity?.imageUrls ?? [];
  const hreflangs = productLocales(product, locale);
  const isActive = product.productStatus === ProductStatus.ProductStatusActive;
  const indexable = isActive && !product.unlisted && hreflangs.includes(locale);

  const seo = triplaboSeo(t, locale, url, {
    title,
    description,
    ogType: 'product',
    ...(images[0] ? { ogImage: images[0] } : {}),
    hreflangs,
    robots: indexable ? 'index, follow' : 'noindex, follow'
  });

  const showOtherCurrencies =
    site.siteLayout?.headerConfig?.showOtherCurrencies ?? false;
  const unitPrices = (activity?.plans ?? [])
    .flatMap(plan => plan.unitItems ?? [])
    .map(unit => resolveUnitAmount(unit, showOtherCurrencies))
    .filter(amount => amount > 0);

  const { origin } = new URL(url);
  // Built the same way `buildSeoMeta` builds the canonical — locale segment
  // from the rendered locale, no query — so the JSON-LD `url` and the
  // canonical link always agree.
  const pageUrl = `${origin}${buildLocalizedPath(`product/${product.id}`, locale)}`;
  const homeUrl = `${origin}${buildLocalizedPath('', locale)}`;
  const siteName = t('triplabo.home.title');
  const location =
    product.productContent?.location ?? product.productContent?.locations?.[0];

  const jsonLd = productPageJsonLd({
    siteName,
    pageUrl,
    product: {
      name: title,
      description: markdownToPlainText(activity?.description ?? ''),
      sku: product.id,
      category: getActivityCategoryTags(activity?.categories, t)[0],
      images
    },
    offer: {
      price: unitPrices.length ? Math.min(...unitPrices) : undefined,
      highPrice: unitPrices.length ? Math.max(...unitPrices) : undefined,
      offerCount: unitPrices.length,
      currency,
      inStock: isActive
    },
    place: location
      ? {
          name: location.name,
          address: location.address,
          lat: location.coordinates?.lat,
          lon: location.coordinates?.lon
        }
      : undefined,
    breadcrumbs: [
      { name: siteName, url: homeUrl },
      { name: title, url: pageUrl }
    ],
    faqs: activity?.faqs?.map(faq => ({
      question: faq.question ?? '',
      answer: markdownToPlainText(faq.answer ?? '')
    }))
  });

  return [
    ...seo,
    // schema-dts node types do not line up with react-router's `LdJsonObject`
    // index signature, though every node is plain JSON by construction.
    ...jsonLd.map(node => ({ 'script:ld+json': node }) as MetaDescriptor)
  ];
}
